import dayjs, { Dayjs } from "dayjs";
import { getBitcoinPrice, getBuySellPoint, getTrendZone } from "./cryptoData/bitcoin";
import { getEthPrice } from "./cryptoData/ethereum";
import { getSolPrice } from "./cryptoData/solana";
import { BuySellType, TrendType } from "@prisma/client";

export interface PerformanceProofData {
  title: string;
  dates: string[];
  values: number[];
  buySellPoints: { date: string; type: BuySellType }[];
  trendZones: { startDate: string; endDate: string; trend: TrendType }[];
  updatedAt: Dayjs;
  hasSub: boolean;
}

export interface PerformanceProofMetric {
  getData: () => Promise<PerformanceProofData>;
}

export const BtcPerfProof: PerformanceProofMetric = {
  getData: async () => {
    const startDate = dayjs().subtract(1, "year");
    const endDate = dayjs();
    const startDateStr = startDate.format("YYYY-MM-DD");
    const endDateStr = endDate.format("YYYY-MM-DD");
    const data = await getBitcoinPrice(startDateStr, endDateStr);
    const dates = data.map((item) => dayjs(item.date).format("YYYY-MM-DD"));
    const prices = data.map((item) => item.price);
    const buySellPoint = await getBuySellPoint("btc", startDateStr, endDateStr);
    const {trendZone, hasSub} = await getTrendZone("btc", startDateStr, endDateStr);

    // 获取 buySellPoint 和 trendZone 的最大 updatedAt
    let updatedAt = dayjs();
    if (buySellPoint.length > 0 && trendZone.length > 0) {
      const buySellUpdated = dayjs(buySellPoint[buySellPoint.length - 1].updateAt);
      const trendZoneUpdated = dayjs(trendZone[trendZone.length - 1].updateAt);
      updatedAt = buySellUpdated.isAfter(trendZoneUpdated) ? buySellUpdated : trendZoneUpdated;
    } else if (buySellPoint.length > 0) {
      updatedAt = dayjs(buySellPoint[buySellPoint.length - 1].updateAt);
    } else if (trendZone.length > 0) {
      updatedAt = dayjs(trendZone[trendZone.length - 1].updateAt);
    }

    return {
      title: "Metrics.bitcoinPrice.title",
      dates,
      values: prices,
      buySellPoints: buySellPoint.map((item) => ({
        date: dayjs(item.date).format("YYYY-MM-DD"),
        type: item.type,
      })),
      trendZones: trendZone.map((item) => ({
        startDate: dayjs(item.startDate).format("YYYY-MM-DD"),
        endDate: dayjs(item.endDate).format("YYYY-MM-DD"),
        trend: item.type,
      })),
      updatedAt,
      hasSub
    };
  },
};

export const EthPerfProof: PerformanceProofMetric = {
  getData: async () => {
    const startDate = dayjs().subtract(1, "year");
    const endDate = dayjs();
    const startDateStr = startDate.format("YYYY-MM-DD");
    const endDateStr = endDate.format("YYYY-MM-DD");
    const data = await getEthPrice(startDateStr, endDateStr);
    const dates = data.map((item) => dayjs(item.date).format("YYYY-MM-DD"));
    const prices = data.map((item) => item.price);
    const buySellPoint = await getBuySellPoint("eth", startDateStr, endDateStr);
    const {trendZone, hasSub} = await getTrendZone("eth", startDateStr, endDateStr);

    let updatedAt = dayjs();
    if (buySellPoint.length > 0 && trendZone.length > 0) {
      const buySellUpdated = dayjs(buySellPoint[buySellPoint.length - 1].updateAt);
      const trendZoneUpdated = dayjs(trendZone[trendZone.length - 1].updateAt);
      updatedAt = buySellUpdated.isAfter(trendZoneUpdated) ? buySellUpdated : trendZoneUpdated;
    } else if (buySellPoint.length > 0) {
      updatedAt = dayjs(buySellPoint[buySellPoint.length - 1].updateAt);
    } else if (trendZone.length > 0) {
      updatedAt = dayjs(trendZone[trendZone.length - 1].updateAt);
    }

    return {
      title: "Metrics.ethPrice.title",
      dates,
      values: prices,
      buySellPoints: buySellPoint.map((item) => ({
        date: dayjs(item.date).format("YYYY-MM-DD"),
        type: item.type,
      })),
      trendZones: trendZone.map((item) => ({
        startDate: dayjs(item.startDate).format("YYYY-MM-DD"),
        endDate: dayjs(item.endDate).format("YYYY-MM-DD"),
        trend: item.type,
      })),
      updatedAt,
      hasSub
    };
  },
};

// 新增 SOL
export const SolPerfProof: PerformanceProofMetric = {
  getData: async () => {
    const startDate = dayjs().subtract(1, "year");
    const endDate = dayjs();
    const startDateStr = startDate.format("YYYY-MM-DD");
    const endDateStr = endDate.format("YYYY-MM-DD");
    const data = await getSolPrice(startDateStr, endDateStr);
    const dates = data.map((item) => dayjs(item.date).format("YYYY-MM-DD"));
    const prices = data.map((item) => item.price);
    const buySellPoint = await getBuySellPoint("sol", startDateStr, endDateStr);
    const {trendZone, hasSub} = await getTrendZone("sol", startDateStr, endDateStr);

    let updatedAt = dayjs();
    if (buySellPoint.length > 0 && trendZone.length > 0) {
      const buySellUpdated = dayjs(buySellPoint[buySellPoint.length - 1].updateAt);
      const trendZoneUpdated = dayjs(trendZone[trendZone.length - 1].updateAt);
      updatedAt = buySellUpdated.isAfter(trendZoneUpdated) ? buySellUpdated : trendZoneUpdated;
    } else if (buySellPoint.length > 0) {
      updatedAt = dayjs(buySellPoint[buySellPoint.length - 1].updateAt);
    } else if (trendZone.length > 0) {
      updatedAt = dayjs(trendZone[trendZone.length - 1].updateAt);
    }

    return {
      title: "Metrics.solPrice.title",
      dates,
      values: prices,
      buySellPoints: buySellPoint.map((item) => ({
        date: dayjs(item.date).format("YYYY-MM-DD"),
        type: item.type,
      })),
      trendZones: trendZone.map((item) => ({
        startDate: dayjs(item.startDate).format("YYYY-MM-DD"),
        endDate: dayjs(item.endDate).format("YYYY-MM-DD"),
        trend: item.type,
      })),
      updatedAt,
      hasSub
    };
  },
};
